import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Brain, Calculator, CheckCircle, Network, Shield, Workflow } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GrandeEcoleSpot from './GrandeEcoleSpot';
import { Header } from './HomePageLab';

// ─────────────────────────────────────────────────────────────────────────────
// DONNÉES — Indicateurs socio-économiques, pas financiers
// ─────────────────────────────────────────────────────────────────────────────

const STATS_SOURCE = 'Documentation publique · Projets · Cohortes pilotes · Expériences · 2006–2025';

// Ce que le terrain a produit — faits, pas promesses
const OBSERVATIONS = [
  {
    n: '01',
    potentiel: '−40%',
    accentColor: '#10b981',
    titre: 'Blocages décisionnels résolus en environnement complexe',
    corps: "Ils se réduisent lorsque les trajectoires sont documentées et que les bifurcations sont rendues visibles. Une observation répétée sur quinze ans de terrain.",
    source: STATS_SOURCE,
  },
  {
    n: '02',
    potentiel: '+65%',
    accentColor: '#f59e0b',
    titre: 'Collaboration transversale observable vs organisations témoins',
    corps: "Elle émerge lorsque les silos sont cartographiés et que les interdépendances sont rendues lisibles, conséquence de la mise en visibilité.",
    source: STATS_SOURCE,
  },
  {
    n: '03',
    potentiel: '73%',
    accentColor: '#22d3ee',
    titre: 'Capacité à naviguer l\'incertitude sans paralysie',
    corps: "Elle se développe par la pratique de la décision en situation complexe, documentée et suivie dans le temps. C'est une compétence outillée.",
    source: 'DREETS Centre-Val de Loire · FSE 2011–2014',
  },
];

// Ce que l'architecture rend possible — sans prescrire
const ARCHITECTURE = [
  {
    icon: <Network size={24} strokeWidth={1.5} />,
    titre: 'Cartographie des interdépendances',
    corps: "La complexité systémique naît des interdépendances invisibles. PESACORE rend visibles les connexions entre les décisions, les départements et les conséquences. Chaque bifurcation est documentée et chaque trajectoire laisse une trace consultable.",
    detail: 'Corpus terrain · Bifurcations documentées',
    accent: '#ffac71',
  },
  {
    icon: <Brain size={24} strokeWidth={1.5} />,
    titre: 'Intelligence collective distribuée',
    corps: "Un environnement qui transforme la connaissance tacite en capital organisationnel. Les équipes qui accompagnent les cohortes disposent d'analytics de processus décisionnel, de gestion multi-cohortes et de certifications.",
    detail: 'Process mining · Analytics temps réel',
    accent: '#f59e0b',
  },
  {
    icon: <Workflow size={24} strokeWidth={1.5} />,
    titre: 'Traçabilité des décisions complexes',
    corps: "Chaque décision prise en simulation est enregistrée — hésitations, bifurcations, retours en arrière. Ce corpus devient une preuve consultable de la manière dont l'organisation pense. Pluôt qu'une évaluation de satisfaction nous proposons une documentation des processus cognitifs.",
    detail: `Données exploitables · ${STATS_SOURCE}`,
    accent: '#10b981',
  },
  {
    icon: <Shield size={24} strokeWidth={1.5} />,
    titre: 'Souveraineté cognitive',
    corps: "Hébergement France, RGPD natif, audit trail complet. L'organisation qui déploie PESACORE conserve la propriété de ses données de parcours et de ses traces décisionnelles. La connaissance produite reste dans l'organisation.",
    detail: 'Hébergement France · SSO roadmap 2026',
    accent: '#22d3ee',
  },
];

// Indicateurs de complexité systémique — l'ennemi commun
const COMPLEXITY_INDICATORS = [
  { label: 'COÛT.COMPLEXITÉ.AN',      val: '€520k',  trend: 'up',   color: '#ef4444' },
  { label: 'SILOS.ORGANISATIONNELS',   val: '78%',    trend: 'up',   color: '#ffac71' },
  { label: 'DÉCISIONS.BLOQUÉES',      val: '43%',    trend: 'up',   color: '#ef4444' },
  { label: 'ABNÉGATION',         val: '+2.8×',  trend: 'up',   color: '#10b981' },
  { label: 'COLLAB.TRANSV',      val: '+65%',   trend: 'up',   color: '#10b981' },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANTS
// ─────────────────────────────────────────────────────────────────────────────

const ComplexityTicker = () => {
  return (
    <div className="overflow-hidden py-2 bg-black/40"
      style={{ borderTop: '1px solid rgba(249,115,22,0.08)', borderBottom: '1px solid rgba(249,115,22,0.08)' }}>
      <motion.div className="flex gap-10 whitespace-nowrap"
        animate={{ x: ['0%', '-50%'] }} transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}>
        {[...COMPLEXITY_INDICATORS, ...COMPLEXITY_INDICATORS].map((m, i) => (
          <div key={i} className="flex items-center gap-2 shrink-0">
            <span className="text-[9px] font-mono text-zinc-200 uppercase tracking-widest">{m.label}</span>
            <span className="text-[10px] font-mono font-bold" style={{ color: m.color }}>{m.val}</span>
            <span className="text-[8px]" style={{ color: m.color }}>{m.trend === 'up' ? '▲' : '▼'}</span>
            <span className="text-zinc-800 mx-2">·</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const ComplexityDiagnostic = ({ complexity }) => {
  const cells = [
    { label: 'Foyers de complexité identifiés', val: `${complexity.foyersComplexite}`,       sub: 'interdépendances critiques',   accent: 'rgba(249,115,22,0.7)' },
    { label: 'Décisions sous contrainte / an',   val: `${complexity.decisionsSousContrainte}`, sub: 'sans cadre de résolution',    accent: 'rgba(239,68,68,0.7)' },
    { label: 'Coût cognitif estimé',              val: `€${Math.round(complexity.coutCognitif / 1000)}k`, sub: 'réunions, boucles, blocages', accent: 'rgba(249,115,22,0.7)' },
    { label: 'Coût systémique annuel',            val: `€${Math.round(complexity.coutSystemique / 1000)}k`, sub: 'estimation conservative',    accent: 'rgba(239,68,68,0.9)' },
    { label: 'Gain PESACORE (−40% obs.)',             val: `€${Math.round(complexity.gainPESA / 1000)}k`,  sub: STATS_SOURCE,                  accent: 'rgba(16,185,129,0.9)' },
    { label: 'Abnégation évaluée',                 val: `×${complexity.multiplierAbnegation}`,            sub: 'capacité de décision',        accent: 'rgba(245,158,11,0.9)' },
  ];
  return (
    <div className="grid grid-cols-3 gap-px" style={{ background: 'rgba(249,115,22,0.05)' }}>
      {cells.map((c, i) => (
        <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 0.6 + i * 0.1 }}
          className="p-4 bg-[#020617] relative"
        >
          <div className="text-[8px] font-mono text-zinc-200 uppercase tracking-widest mb-1">{c.label}</div>
          <div className="text-2xl font-black font-mono leading-none mb-1" style={{ color: c.accent }}>{c.val}</div>
          <div className="text-[8px] font-mono text-zinc-200 uppercase tracking-wider italic">{c.sub}</div>
          <motion.div className="absolute top-2 right-2 w-1 h-1 rounded-full" style={{ background: c.accent }}
            animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2 + i * 0.3, repeat: Infinity }} />
        </motion.div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE PRINCIPALE
// ─────────────────────────────────────────────────────────────────────────────

export default function LandingPageEntreprise() {
  const navigate = useNavigate();
  const [complexityInputs, setComplexityInputs]           = useState({ effectif: 100, silos: 5, decisonsBloquees: 40 });
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [showSpot, setShowSpot]             = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const complexity = useMemo(() => {
    const foyersComplexite        = complexityInputs.silos * 3;
    const decisionsSousContrainte = Math.round((complexityInputs.effectif * complexityInputs.decisonsBloquees) / 100);
    const coutCognitif            = decisionsSousContrainte * 8500; // coût moyen par décision bloquée
    const coutSystemique          = coutCognitif * 1.6; // effet systémique multiplicateur
    const gainPESA                = coutSystemique * 0.4; // -40% de blocages observés
    const coutPESA                = 9600;
    const multiplierAbnegation    = coutPESA > 0 ? (gainPESA / coutPESA).toFixed(1) : 0;
    return {
      foyersComplexite,
      decisionsSousContrainte,
      coutCognitif: Math.round(coutCognitif),
      coutSystemique: Math.round(coutSystemique),
      gainPESA: Math.round(gainPESA),
      multiplierAbnegation,
    };
  }, [complexityInputs.effectif, complexityInputs.silos, complexityInputs.decisonsBloquees]);

  useEffect(() => {
      const check = () => setIsMobile(window.innerWidth < 600);
      check();
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }, []);

  return (
    <div className="min-h-screen bg-[#020617] text-orange-50 relative overflow-hidden"
      style={{ fontFamily: 'monospace' }}>

      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(rgba(249,115,22,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.025) 1px, transparent 1px)`,
        backgroundSize: '32px 32px',
      }} />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_20%_30%,rgba(249,115,22,0.06),transparent_55%)]" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_80%_70%,rgba(245,158,11,0.04),transparent_55%)]" />

      <Header />
      <div className="pt-20"><ComplexityTicker /></div>

      {/* ══ POSITIONNEMENT ══
          La complexité systémique est l'ennemi commun.
          PESACORE n'est pas un "dispositif de formation".
          C'est une architecture qui équipe les organisations
          pour traverser la complexité sans se perdre.
      ══ */}
      <section className="relative px-4 sm:px-6 py-14 sm:py-22">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-14 items-start">

          {/* Gauche */}
          <div className="lg:col-span-7 order-2 lg:order-1">

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-8">
              <div className="h-px w-6" style={{ background: 'rgba(249,115,22,0.4)' }} />
              <span className="text-[9px] font-mono text-orange-200 uppercase tracking-[0.3em]">
                PESACORE Architect · Complexité systémique
              </span>
            </motion.div>

            {/* Titre — ce que c'est, sans vendre */}
            <div className="mb-8">
              <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="font-black uppercase tracking-tight leading-[0.95] text-white mb-3"
                style={{ fontSize: 'clamp(1.9rem, 6vw, 3.5rem)' }}>
                La complexité n'est pas
              </motion.h1>
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}>
                <span className="font-black italic tracking-tight leading-[0.95]"
                  style={{
                    fontSize: 'clamp(1.9rem, 6vw, 3.5rem)',
                    fontFamily: 'Georgia, serif',
                    background: 'linear-gradient(135deg, #ffac71 0%, #f59e0b 50%, #ffac71 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>
                  une fatalité.
                </span>
              </motion.div>
            </div>

            {/* Corps — description de l'architecture, pas pitch */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="mb-7 max-w-xl space-y-4">
              <p className="text-orange-100/85 leading-relaxed"
                style={{ fontSize: 'clamp(0.82rem, 2.5vw, 0.93rem)', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                La complexité systémique coûte plus cher que tous les budgets formation réunis.
                Elle bloque les décisions, isole les équipes et épuise les organisations.
                PESACORE est une architecture conçue pour la traverser, avec lucidité, collectivement.
              </p>
              <p className="text-orange-100/65 leading-relaxed"
                style={{ fontSize: 'clamp(0.78rem, 2vw, 0.88rem)', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                Ce n'est pas un outil de plus. C'est un cadre dans lequel les interdépendances
                deviennent visibles, les décisions deviennent traçables, et l'intelligence
                collective devient un capital documenté.
              </p>
            </motion.div>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
              className="text-[10px] font-mono text-zinc-200 uppercase tracking-wider mb-7">
              Déployé depuis 2011 · Validé FSE · Mission Locale · Cohortes entreprise depuis 2023
            </motion.p>

            {/* Actions — sans urgence */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-3 mb-7">
              <button onClick={() => navigate('/contact?from=entreprise&sujet=echange')}
                className="group flex items-center justify-center gap-3 px-7 py-3.5 w-full sm:w-auto transition-all"
                style={{ background: 'rgba(249,115,22,0.09)', border: '1px solid rgba(249,115,22,0.32)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(249,115,22,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(249,115,22,0.09)'}>
                <span className="text-sm font-black uppercase tracking-wider text-white">
                  Prendre contact
                </span>
                <ArrowRight size={14} className="text-orange-400/70 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => setShowDiagnostic(true)}
                className="flex items-center justify-center gap-2 px-6 py-3.5 transition-all w-full sm:w-auto"
                style={{ border: '1px solid rgba(245,158,11,0.12)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.25)'; e.currentTarget.style.background = 'rgba(245,158,11,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.12)'; e.currentTarget.style.background = 'transparent'; }}>
                <Calculator size={14} className="text-amber-400/60" />
                <span className="text-xs uppercase tracking-widest text-zinc-300">
                  Diagnostic de complexité
                </span>
              </button>
            </motion.div>

            {/* Ce que le terrain a produit — données sourçées */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }}
              className="overflow-x-auto"
              style={{ border: '1px solid rgba(249,115,22,0.08)' }}>
              {OBSERVATIONS.map((obs, i) => (
                <div key={i} className="flex items-center gap-0"
                  style={{ borderBottom: i < OBSERVATIONS.length - 1 ? '1px solid rgba(249,115,22,0.08)' : 'none' }}>
                  <div className="px-4 py-3 min-w-[80px] text-center"
                    style={{ borderRight: '1px solid rgba(249,115,22,0.08)' }}>
                    <div className="text-lg font-black leading-none" style={{ color: obs.accentColor }}>
                      {obs.potentiel}
                    </div>
                  </div>
                  <div className="px-4 py-2 flex-1">
                    <div className="text-[9px] font-mono text-zinc-400 uppercase tracking-wider leading-tight">
                      {obs.titre}
                    </div>
                    <div className="text-[8px] font-mono text-zinc-200 italic mt-0.5">{obs.source}</div>
                  </div>
                </div>
              ))}
            </motion.div>
            <p className="text-[8px] font-mono text-zinc-200 uppercase tracking-wider mt-2 italic">
              Observations issues d'expériences répétées et de cohortes réelles · Les résultats varient selon le contexte organisationnel
            </p>
          </div>

          {/* Droite — diagnostic de complexité (ex-estimateur) */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="lg:col-span-5 order-1 lg:order-2">
            <div className="flex items-center justify-between px-4 py-2.5 bg-black/50"
              style={{ border: '1px solid rgba(249,115,22,0.15)', borderBottom: 'none' }}>
              <div className="flex items-center gap-2">
                <motion.div className="w-1.5 h-1.5 rounded-full bg-red-500"
                  animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1.2, repeat: Infinity }} />
                <span className="text-[9px] font-mono text-orange-400/60 uppercase tracking-[0.25em]">
                  Complexité systémique · Estimation
                </span>
              </div>
              <span className="text-[8px] font-mono text-zinc-200 uppercase">PESACORE_ENT v1.0</span>
            </div>
            <ComplexityDiagnostic complexity={complexity} />
            <div className="px-3 py-2 bg-black/30"
              style={{ border: '1px solid rgba(249,115,22,0.08)', borderTop: 'none' }}>
              <p className="text-[8px] font-mono text-zinc-200 uppercase tracking-wider italic">
                Basé sur −40% blocages décisionnels observés · {STATS_SOURCE} · Les résultats varient
              </p>
            </div>
            <button onClick={() => setShowDiagnostic(true)}
              className="mt-3 w-full py-2.5 text-[10px] font-mono text-zinc-200 hover:text-zinc-400 transition-all uppercase tracking-widest"
              style={{ border: '1px solid rgba(82,82,91,0.35)' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(82,82,91,0.6)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(82,82,91,0.35)'}>
              Évaluer votre niveau de complexité →
            </button>
          </motion.div>
        </div>
      </section>

      {/* ══ CE QUE LE TERRAIN DOCUMENTE ══
          Trois observations factuelles — sans extrapolation ni conclusion forcée.
          Le lecteur qui se reconnaît dans le contexte comprend seul la pertinence.
      ══ */}
      <section className="py-14 sm:py-20 px-4 sm:px-6"
        style={{ borderTop: '1px solid rgba(249,115,22,0.06)', background: 'rgba(2,6,23,0.8)' }}>
        <div className="max-w-6xl mx-auto">

          <div className="flex items-center gap-4 mb-10 sm:mb-14">
            <div className="text-[9px] font-mono text-orange-200 uppercase tracking-[0.3em] whitespace-nowrap">
              Ce que le terrain produit
            </div>
            <div className="h-px flex-1 bg-orange-500/8" />
            <div className="text-[9px] font-mono text-zinc-300 uppercase tracking-wider hidden sm:block"> {STATS_SOURCE}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px"
            style={{ background: 'rgba(249,115,22,0.05)' }}>
            {OBSERVATIONS.map((obs, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="p-7 sm:p-10 bg-[#020617]">

                {/* potentiel — la donnée parle avant le texte */}
                <div className="text-5xl sm:text-6xl font-black font-mono leading-none mb-4"
                  style={{ color: obs.accentColor }}>
                  {obs.potentiel}
                </div>

                <div className="flex items-start justify-between mb-5">
                  <span className="text-[9px] font-mono text-orange-600/35 tracking-widest">{obs.n} ·</span>
                  <span className="text-[8px] font-mono text-zinc-200 italic">{obs.source}</span>
                </div>

                <h3 className="text-sm font-black uppercase tracking-tight text-white mb-4 leading-snug">
                  {obs.titre}
                </h3>
                <p className="text-zinc-300 text-xs leading-relaxed"
                  style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                  {obs.corps}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 px-4 py-3"
            style={{ border: '1px solid rgba(249,115,22,0.06)', borderTop: 'none' }}>
            <p className="text-[9px] font-mono text-zinc-200 uppercase tracking-wider italic text-center">
              Ces observations ne constituent pas des garanties de résultat. Elles documentent ce qui s'est passé dans des contextes spécifiques entre 2011 et 2025.
            </p>
          </div>
        </div>
      </section>

      {/* ══ DÉMONSTRATION ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="text-[9px] font-mono text-orange-200 uppercase tracking-[0.3em] whitespace-nowrap">
              Architecture · Grande École
            </div>
            <div className="h-px flex-1 bg-orange-500/8" />
          </div>
          <h2 className="font-black uppercase tracking-tight text-white mb-2"
              style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
            L'environnement de pilotage
          </h2>
          <p className="text-zinc-200 font-mono text-xs uppercase tracking-wider mb-6 sm:mb-8">
            Ce que les équipes qui déploient PESACORE ont à disposition pour cartographier la complexité
          </p>
          <div className="relative bg-black/60" style={{
              aspectRatio: isMobile ? undefined : '16/9',
              height: isMobile ? '520px' : undefined,
              border: '1px solid rgba(34,211,238,0.08)',
              overflow: 'hidden',
            }}>
            <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-orange-500/20 z-10" />
            <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-orange-500/20 z-10" />
            <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-amber-500/15 z-10" />
            <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-amber-500/15 z-10" />
            {!showSpot ? (
              <div className="absolute inset-0 flex items-center justify-center cursor-pointer"
                onClick={() => setShowSpot(true)}>
                <div className="text-center">
                  <motion.div animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 2.5, repeat: Infinity }}
                    className="w-14 h-14 mx-auto mb-4 flex items-center justify-center"
                    style={{ border: '1px solid rgba(249,115,22,0.2)', boxShadow: '0 0 20px rgba(249,115,22,0.08)' }}>
                    <ArrowRight size={22} className="text-orange-400/60 ml-1" />
                  </motion.div>
                  <p className="text-zinc-200 font-mono text-[10px] uppercase tracking-widest px-4">
                    Visualiser l'interface
                  </p>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0">
                <GrandeEcoleSpot />                
                <button
                  onClick={() => setShowSpot(false)}
                  className="absolute top-3 right-3 z-50 px-3 py-1.5 bg-black/80 text-orange-400/70 text-xs font-mono border border-orange-500/20 rounded-lg"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══ DESCRIPTION DE L'ARCHITECTURE ══
          Ce que PESACORE met à disposition — pour affronter la complexité.
      ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6"
        style={{ borderTop: '1px solid rgba(249,115,22,0.06)', background: 'rgba(2,6,23,0.8)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6 sm:mb-10">
            <div className="text-[9px] font-mono text-orange-200 uppercase tracking-[0.3em]">
              Ce que PESACORE met à disposition
            </div>
            <div className="h-px flex-1 bg-orange-500/8" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {ARCHITECTURE.map((a, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="p-6 sm:p-8"
                style={{
                  background: 'rgba(2,6,23,0.9)',
                  borderTop: '1px solid rgba(249,115,22,0.06)',
                  borderRight: i % 2 === 0 ? '1px solid rgba(249,115,22,0.06)' : 'none',
                }}>
                <div className="mb-4" style={{ color: a.accent, opacity: 0.75 }}>{a.icon}</div>
                <h3 className="text-sm font-black uppercase tracking-tight text-white mb-3">{a.titre}</h3>
                <p className="text-zinc-300 text-xs leading-relaxed mb-4"
                  style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{a.corps}</p>
                <span className="text-[9px] font-mono uppercase tracking-wider"
                  style={{ color: a.accent, opacity: 0.6, borderBottom: `1px solid ${a.accent}28`, paddingBottom: '2px' }}>
                  {a.detail}
                </span>
              </motion.div>
            ))}
            <div className="col-span-1 md:col-span-2"
              style={{ borderTop: '1px solid rgba(249,115,22,0.06)' }} />
          </div>
        </div>
      </section>

      {/* ══ CONDITIONS D'ACCÈS ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-6 sm:mb-8">
            <div className="text-[9px] font-mono text-orange-200 uppercase tracking-[0.3em]">
              Conditions d'accès
            </div>
            <div className="h-px flex-1 bg-orange-500/8" />
          </div>
          <h2 className="font-black uppercase tracking-tight text-white mb-1"
            style={{ fontSize: 'clamp(1.2rem, 4vw, 1.5rem)' }}>
            Modalités d'engagement
          </h2>
          <p className="text-zinc-200 font-mono text-xs uppercase tracking-wider mb-8 sm:mb-10">
            Deux cadres d'accès selon la maturité et les besoins de l'organisation face à la complexité
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2"
            style={{ border: '1px solid rgba(249,115,22,0.08)' }}>

            {/* Pro */}
            <div className="flex flex-col p-6 sm:p-8"
              style={{ borderBottom: '1px solid rgba(249,115,22,0.08)' }}>
              <div className="text-[9px] font-mono text-zinc-200 uppercase tracking-[0.3em] mb-3">
                Organisation ~ 500 personnes
              </div>
              <h3 className="text-lg font-black uppercase text-white mb-1">Pro</h3>
              <div className="flex items-baseline gap-2 mb-6 mt-3 flex-wrap">
                <span className="text-4xl sm:text-5xl font-black tracking-tight"
                  style={{ background: 'linear-gradient(90deg, #22d3ee, #67e8f9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  €9 600
                </span>
                <span className="text-zinc-200 font-mono text-sm">/an</span>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {[
                  "Jusqu'à 500 collaborateurs",
                  "Architecture Grande École complète",
                  "White-label & personnalisation",
                  "API v1 + intégrations",
                  "Support prioritaire",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-400 text-xs">
                    <CheckCircle size={12} className="text-emerald-500/50 shrink-0" />
                    <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{item}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/contact?from=entreprise&sujet=demo')}
                className="w-full py-3 font-black uppercase text-white text-xs tracking-wider transition-all"
                style={{ border: '1px solid rgba(34,211,238,0.2)', background: 'rgba(34,211,238,0.03)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,211,238,0.07)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,211,238,0.03)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,0.2)'; }}>
                Demander une démonstration
              </button>
            </div>

            {/* Entreprise */}
            <div className="flex flex-col p-6 sm:p-8 relative"
              style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.04), rgba(2,6,23,1))' }}>
              <div className="text-[9px] font-mono text-orange-200 uppercase tracking-[0.3em] mb-3">
                Grands comptes & institutions
              </div>
              <h3 className="text-lg font-black uppercase text-white mb-1">Entreprise</h3>
              <div className="text-2xl sm:text-3xl font-black mb-6 mt-3 tracking-tight"
                style={{ background: 'linear-gradient(90deg, #ffac71, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Sur devis
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {[
                  { t: "Organisations sans limite d'effectif",     ok: true },
                  { t: "Corpus de cas construit sur mesure",       ok: true },
                  { t: "Hébergement France · RGPD complet",        ok: true },
                  { t: "Accompagnement au déploiement",            ok: true },
                  { t: "SSO & SLA enterprise (roadmap 2026)",      ok: false },
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs">
                    <CheckCircle size={12} className={`shrink-0 ${item.ok ? 'text-amber-400/50' : 'text-zinc-200'}`} />
                    <span className={item.ok ? 'text-zinc-300' : 'text-zinc-200'}
                      style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{item.t}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/contact?from=entreprise&sujet=devis')}
                className="w-full py-3 font-black uppercase text-white text-xs tracking-wider transition-all"
                style={{ border: '1px solid rgba(249,115,22,0.3)', background: 'rgba(249,115,22,0.06)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.1)'; e.currentTarget.style.borderColor = 'rgba(249,115,22,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.06)'; e.currentTarget.style.borderColor = 'rgba(249,115,22,0.3)'; }}>
                Demander un devis
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ ÉTAPE SUIVANTE ══
          Une invitation, pas un CTA dramatique.
          La qualification est mutuelle — c'est dit explicitement.
      ══ */}
      <section className="py-12 sm:py-16 px-4 sm:px-6"
        style={{ borderTop: '1px solid rgba(249,115,22,0.06)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="relative p-8 sm:p-14"
            style={{ background: 'rgba(2,6,23,0.9)', border: '1px solid rgba(249,115,22,0.07)' }}>
            <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-orange-500/15" />
            <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-amber-500/12" />

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-8">
              <div>
                <div className="text-[9px] font-mono text-orange-200 uppercase tracking-[0.3em] mb-4">
                  Étape suivante
                </div>
                <h2 className="font-black uppercase tracking-tight text-white mb-3"
                  style={{ fontSize: 'clamp(1.1rem, 3.5vw, 1.35rem)' }}>
                  Un premier échange
                </h2>
                <p className="text-zinc-300 text-sm"
                  style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', maxWidth: '44ch', lineHeight: 1.75 }}>
                  Pour déterminer ensemble si la complexité à laquelle votre organisation
                  fait face correspond à ce que l'architecture PESACORE peut aider à traverser.
                  La qualification est mutuelle — aucune décision n'est prise
                  avant cet échange.
                </p>
                <p className="text-zinc-200 text-[10px] font-mono uppercase tracking-wider mt-4">
                  Sans engagement · Durée estimée 30 minutes · Réponse sous 48h
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:shrink-0">
                <button onClick={() => navigate('/contact?from=entreprise&sujet=echange')}
                  className="group flex items-center justify-center gap-3 px-8 py-3.5 transition-all whitespace-nowrap"
                  style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.28)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.13)'; e.currentTarget.style.borderColor = 'rgba(249,115,22,0.45)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.08)'; e.currentTarget.style.borderColor = 'rgba(249,115,22,0.28)'; }}>
                  <span className="text-sm font-black uppercase tracking-wider text-white">Prendre contact</span>
                  <ArrowRight size={14} className="text-orange-400/70 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => setShowDiagnostic(true)}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 transition-all"
                  style={{ border: '1px solid rgba(82,82,91,0.28)' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(82,82,91,0.5)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(82,82,91,0.28)'}>
                  <Calculator size={13} className="text-zinc-200" />
                  <span className="text-xs uppercase tracking-widest text-zinc-200">
                    Diagnostic de complexité
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ MODAL DIAGNOSTIC DE COMPLEXITÉ ══ */}
      <AnimatePresence>
        {showDiagnostic && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setShowDiagnostic(false)}>
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#020617] p-6 sm:p-8 w-full sm:max-w-lg overflow-y-auto"
              style={{ border: '1px solid rgba(249,115,22,0.2)', maxHeight: '92vh', borderRadius: '12px 12px 0 0' }}>

              <div className="w-10 h-1 bg-zinc-700 rounded-full mx-auto mb-4 sm:hidden" />
              <div className="flex items-center gap-3 mb-2">
                <div className="h-3 w-px bg-orange-500/35" />
                <h3 className="text-base font-black uppercase tracking-tight text-white">Diagnostic de complexité</h3>
              </div>
              <p className="text-zinc-200 text-xs mb-6"
                style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', paddingLeft: '1.2rem' }}>
                Évaluez le coût systémique de la complexité dans votre organisation.
                Projection basée sur les observations des documents publics et cohortes pilotes.
              </p>

              <div className="space-y-5 mb-8">
                {[
                  { label: 'Effectif concerné par les décisions complexes',  key: 'effectif' },
                  { label: 'Nombre de silos organisationnels identifiés',    key: 'silos' },
                  { label: 'Taux de décisions bloquées ou reportées (%)',    key: 'decisonsBloquees' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-[9px] font-mono text-zinc-200 mb-2 uppercase tracking-widest">
                      {f.label}
                    </label>
                    <input type="number" inputMode="numeric"
                      value={complexityInputs[f.key]}
                      onChange={e => setComplexityInputs({ ...complexityInputs, [f.key]: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2.5 bg-black/60 text-white focus:outline-none font-mono text-sm transition-all"
                      style={{ border: '1px solid rgba(82,82,91,0.45)' }}
                      onFocus={e => e.currentTarget.style.borderColor = 'rgba(249,115,22,0.35)'}
                      onBlur={e => e.currentTarget.style.borderColor = 'rgba(82,82,91,0.45)'} />
                  </div>
                ))}
              </div>

              <div className="p-4 sm:p-5 mb-5 text-center"
                style={{ border: '1px solid rgba(249,115,22,0.12)', background: 'rgba(249,115,22,0.03)' }}>
                <div className="text-[9px] font-mono text-orange-400/50 uppercase tracking-widest mb-2">
                  Coût systémique annuel estimé
                </div>
                <div className="text-4xl sm:text-5xl font-black mb-2 tracking-tight"
                  style={{ background: 'linear-gradient(90deg, #ffac71, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  €{complexity.coutSystemique.toLocaleString()}
                </div>
                <div className="text-xs text-zinc-200"
                  style={{ fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>
                  Gain potentiel avec PESACORE : €{complexity.gainPESA.toLocaleString()} (abnégation ×{complexity.multiplierAbnegation})
                </div>
                <p className="text-[8px] font-mono text-zinc-200 uppercase tracking-wider mt-3 italic">
                  Projection · {STATS_SOURCE} · Les résultats individuels varient
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => { setShowDiagnostic(false); navigate('/contact?from=entreprise&sujet=echange'); }}
                  className="flex-1 py-3 font-black uppercase text-white text-xs tracking-wider transition-all"
                  style={{ border: '1px solid rgba(249,115,22,0.3)', background: 'rgba(249,115,22,0.06)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(249,115,22,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(249,115,22,0.06)'}>
                  Prendre contact →
                </button>
                <button onClick={() => setShowDiagnostic(false)}
                  className="py-3 px-6 font-mono text-xs text-zinc-200 transition-all"
                  style={{ border: '1px solid rgba(82,82,91,0.28)' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(82,82,91,0.5)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(82,82,91,0.28)'}>
                  Fermer
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <footer className="py-8 px-6 text-center text-xs text-white font-mono uppercase tracking-widest"
        style={{ borderTop: '1px solid rgba(249,115,22,0.05)', background: 'rgba(2,6,23,0.5)' }}>
        © 2026 · PESACORE Architect · Tous droits réservés.
      </footer>
    </div>
  );
}